import debounce from 'lodash/debounce';

import { isMobile } from '@common/utils/platform';
import { getGTMSubProduct } from '@tracking/components/utils';

function buildThresholdList() {
  const thresholds = [];
  const numSteps = 100;

  for (let i = 1.0; i <= numSteps; i += 1) {
    const ratio = i / numSteps;
    thresholds.push(ratio);
  }

  return thresholds;
}

function getScrollPercent(isHorizontal, element) {
  try {
    const target = element.documentElement ?? element;
    const scrollTopLeft = isHorizontal ? 'scrollLeft' : 'scrollTop';
    const scrollHeightWidth = isHorizontal ? 'scrollWidth' : 'scrollHeight';
    const clientHeightWidth = isHorizontal ? 'clientWidth' : 'clientHeight';
    const scrollAmount = (target[scrollTopLeft])
      / ((target[scrollHeightWidth]) - target[clientHeightWidth]);
    return Math.round(scrollAmount * 100);
  } catch (e) {
    return 0;
  }
}

function scrollHandler(data) {
  const {
    element, scrollPercentage, scrollDirection, verticalScroll,
  } = data;
  if (scrollDirection !== 'up') {
    const elementId = element.getAttribute('data-gtm-element')
      || element.getAttribute('data-gtm-parent') || element.id;
    const elementClass = element.getAttribute('class');
    const pageSubProduct = getGTMSubProduct();
    window.GTMtracker.trackEvent('scrollFormatter', {
      verticalScroll,
      scrollDirection,
      scrollPercentage,
      elementId,
      elementClass,
      subProduct: pageSubProduct,
    });
  }
}

const timeDelay = isMobile() ? 500 : 1000;
const scrollEventHandler = debounce(scrollHandler, timeDelay);

function getScrollData(
  lastScrollPercentage = 0, isHorizontal = false, element = document,
) {
  const scrollPercentage = getScrollPercent(isHorizontal, element);

  const scrollDiff = scrollPercentage - lastScrollPercentage;
  let scrollDirection = scrollDiff > 0 ? 'down' : 'up';
  if (isHorizontal) {
    scrollDirection = scrollDiff > 0 ? 'right' : 'left';
  }
  return { scrollDirection, scrollPercentage, scrollDiff };
}

// currently being used only for tracking vertical scroll
function intersectionHandler(scrollableElement, isVertical = true) {
  let lastScrollPercentage = 0;

  return function (entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const {
          scrollPercentage, scrollDirection, scrollDiff,
        } = getScrollData(lastScrollPercentage, !isVertical, scrollableElement);

        if (Math.abs(scrollDiff) > 1) {
          lastScrollPercentage = scrollPercentage;
          scrollEventHandler({
            element: entry.target,
            scrollPercentage,
            scrollDirection,
            verticalScroll: true,
          });
        }
      }
    });
  };
}

// currently being used only for tracking vertical scroll
export function setupIntersectionObserver(scrollableElement, isVertical) {
  const options = {
    root: null,
    rootMargin: '0px',
    threshold: buildThresholdList(),
  };

  const intersectionHandlerFn = intersectionHandler(
    scrollableElement,
    isVertical,
  );

  return new IntersectionObserver(intersectionHandlerFn, options);
}

export function trackScrollHandler(isVertical = false) {
  let lastScrollPercentage = 0;

  return function (event) {
    const {
      scrollPercentage, scrollDirection, scrollDiff,
    } = getScrollData(lastScrollPercentage, !isVertical, event.target);
    if (Math.abs(scrollDiff) < 1) {
      return;
    }
    lastScrollPercentage = scrollPercentage;
    scrollEventHandler({
      element: event.target,
      scrollPercentage,
      scrollDirection,
      verticalScroll: isVertical,
    });
  };
}
